import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { DownloadOutlined, VisibilityOutlined } from "@mui/icons-material";
import {
  collection,
  DocumentData,
  getDocs,
  getFirestore,
  orderBy,
  query,
} from "firebase/firestore";
import { getDownloadURL, ref, getStorage } from "firebase/storage";
import MainLayout from "../../layouts/main_layout/main_layout";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../providers/theme/colors/colors";
import EditorailBoard from "./editorial_board";
import EditorsTimeline from "./editors_timeline";
import { YuvagaazEmagazine, YuvagaazMagazine } from "./yuvagaaz_data";

type YuvagaazItem = {
  id?: number | string;
  docId?: string;
  path?: string;
  pdfUrl?: string;
  year: string;
  title?: string;
  coverImage?: string;
  releaseDate?: string;
};

const coverImageByYear: Record<string, string> = {
  "2026": "/images/yuvagaazEditors/yuvagaaz_2026.jpg",
  "2025": "/images/yuvagaazEditors/yuvagaaz_2025.jpg",
};

const defaultStaticMagazines: YuvagaazItem[] = [
  {
    id: "static-2026",
    year: "2026",
    title: "Yuvagaaz Magazine 2026",
    pdfUrl: "yuvagaaz/magazines/yuvagaaz2026.pdf",
    coverImage: "/images/yuvagaazEditors/yuvagaaz_2026.jpg",
    releaseDate: "2026-01-01",
  },
  {
    id: "static-2025",
    year: "2025",
    title: "Yuvagaaz Magazine 2025",
    pdfUrl: "yuvagaaz/magazines/yuvagaaz2025.pdf",
    coverImage: "/images/yuvagaazEditors/yuvagaaz_2025.jpg",
    releaseDate: "2025-01-01",
  },
];

function YuvaGaaz() {
  const [files, setFiles] = useState<YuvagaazItem[]>([]);
  const [eMagazines, setEMagazines] = useState<YuvagaazEmagazine[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const [activeEMagazineId, setActiveEMagazineId] = useState<string | null>(null);
  const firestore = getFirestore();

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const [legacySnapshot, magazineSnapshot, eMagazineSnapshot] = await Promise.all([
          getDocs(collection(firestore, "yuvagaaz")),
          getDocs(query(collection(firestore, "yuvagaaz_magazines"), orderBy("releaseDate", "desc"))),
          getDocs(query(collection(firestore, "yuvagaaz_emagazines"), orderBy("releaseDate", "desc"))),
        ]);

        const legacyFiles = legacySnapshot.docs
          .map((doc) => doc.data() as DocumentData)
          .flatMap((doc) => doc?.yuvagaazList || []);
        const managedFiles = magazineSnapshot.docs.map((doc) => ({
          docId: doc.id,
          ...(doc.data() as Omit<YuvagaazMagazine, "docId">),
        }));
        const fetchedFiles = [...managedFiles, ...legacyFiles];
        const existingYears = new Set(fetchedFiles.map((f: YuvagaazItem) => f.year));
        const missingDefaults = defaultStaticMagazines.filter((def) => !existingYears.has(def.year));

        const filesList = [...fetchedFiles, ...missingDefaults].sort((itemA: YuvagaazItem, itemB: YuvagaazItem) => {
          const dateA = new Date(itemA.releaseDate || `${itemA.year}-01-01`).getTime();
          const dateB = new Date(itemB.releaseDate || `${itemB.year}-01-01`).getTime();
          return dateB - dateA;
        });
        setFiles(filesList);

        setEMagazines(
          eMagazineSnapshot.docs.map((doc) => ({
            docId: doc.id,
            ...(doc.data() as Omit<YuvagaazEmagazine, "docId">),
          })),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [firestore]);

  const resolvePdfUrl = async (path: string) => {
    if (!path) {
      throw new Error("Missing PDF path");
    }

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    const storage = getStorage();
    return getDownloadURL(ref(storage, path));
  };

  const handleOpen = async (path: string) => {
    const pdfUrl = await resolvePdfUrl(path);
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (path: string, fileName: string) => {
    const pdfUrl = await resolvePdfUrl(path);
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `${fileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCoverImage = (item: YuvagaazItem) => coverImageByYear[item.year];

  return (
    <MainLayout>
      <Box my={{ xs: 4, md: 6 }} px={{ xs: 1, sm: 0 }}>
        <Card
          sx={{
            mb: 5,
            borderRadius: { xs: 3, md: 4 },
            color: "#fff",
            overflow: "hidden",
            background:
              "radial-gradient(circle at top left, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 28%), linear-gradient(135deg, #0b466f 0%, #1f6ea9 45%, #54a9dd 100%)",
            boxShadow: "0 22px 48px rgba(10, 53, 89, 0.2)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{ fontSize: { xs: 30, md: 42 }, mb: 1.5 }}
                >
                  Yuvagaaz Magazine
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: 760,
                    opacity: 0.92,
                    lineHeight: 1.8,
                    fontSize: { xs: 14, sm: 16 },
                  }}
                >
                  Explore the latest editions, revisit previous issues, and keep the
                  voice of the youth ministry close at hand.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    ml: { md: "auto" },
                    px: 3,
                    py: 2.5,
                    borderRadius: { xs: 2.5, md: 3 },
                    width: "100%",
                    maxWidth: { xs: "100%", md: 280 },
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.12) 100%)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.24)",
                  }}
                >
                  <Typography variant="overline" sx={{ letterSpacing: 1.4 }}>
                    Archive
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>
                    {files.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.92 }}>
                    Magazine editions available to view and download
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box mb={4}>
          <Typography
            variant="h5"
            fontWeight={700}
            color={MAIN_THEME_COLOR}
            gutterBottom
          >
            Magazine Archive
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Open any edition in a new tab or download it directly.
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {files.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.docId || item.id || `${item.year}-${item.title}`}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: { xs: 3, md: 4 },
                    overflow: "hidden",
                    border: "1px solid rgba(56, 136, 194, 0.14)",
                    boxShadow: "0 14px 32px rgba(9, 51, 84, 0.09)",
                    background:
                      "linear-gradient(180deg, rgba(247, 251, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)",
                  }}
                >
                  {item.coverImage || getCoverImage(item) ? (
                    <Box sx={{ px: 2, pt: 2 }}>
                      <Box
                        sx={{
                          width: "100%",
                          borderRadius: 3,
                          border: `1px solid ${SECONDARY_THEME_COLOR}55`,
                          boxShadow: "0 16px 28px rgba(9, 51, 84, 0.14)",
                          backgroundColor: "#ffffff",
                          aspectRatio: "3 / 4",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          p: { xs: 1, sm: 1.25 },
                        }}
                      >
                        <Box
                          component="img"
                          src={item.coverImage || getCoverImage(item)}
                          alt={`Yuvagaaz ${item.year} cover`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            objectFit: "contain",
                            objectPosition: "center center",
                          }}
                        />
                      </Box>
                    </Box>
                  ) : null}
                  <CardContent
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: MAIN_THEME_COLOR,
                        letterSpacing: 1.3,
                        fontWeight: 800,
                      }}
                    >
                      Edition
                    </Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
                      {item.year}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, lineHeight: 1.7 }}
                    >
                      {item.title || `Yuvagaaz Magazine ${item.year}`}
                    </Typography>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<VisibilityOutlined />}
                        onClick={async () => {
                          setActiveYear(item.year);
                          try {
                            await handleOpen(item.pdfUrl || item.path || "");
                          } finally {
                            setActiveYear(null);
                          }
                        }}
                      >
                        {activeYear === item.year ? "Opening..." : "View"}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<DownloadOutlined />}
                        onClick={async () => {
                          setActiveYear(item.year);
                          try {
                            await handleDownload(item.pdfUrl || item.path || "", `Yuvagaaz-${item.year}`);
                          } finally {
                            setActiveYear(null);
                          }
                        }}
                      >
                        {activeYear === item.year ? "Preparing..." : "Download"}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && files.length === 0 ? (
          <Box
            mt={2}
            py={6}
            textAlign="center"
            border={`1px dashed ${SECONDARY_THEME_COLOR}`}
            borderRadius={3}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom>
              No magazine editions found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add magazine entries from the Yuvagaaz admin page to show editions here.
            </Typography>
          </Box>
        ) : null}

        <Box mt={7} mb={4}>
          <Typography
            variant="h5"
            fontWeight={700}
            color={MAIN_THEME_COLOR}
            gutterBottom
          >
            E-Magazine Library
          </Typography>
          <Typography variant="body1" color="text.secondary">
            
          </Typography>
        </Box>

        {loading ? null : eMagazines.length === 0 ? (
          <Box
            py={6}
            textAlign="center"
            border={`1px dashed ${SECONDARY_THEME_COLOR}`}
            borderRadius={3}
            mb={6}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom>
              No e-magazines published yet
            </Typography>
            
          </Box>
        ) : (
          <Grid container spacing={3} mb={6}>
            {eMagazines.map((item) => {
              const itemId = item.docId || item.title;
              return (
                <Grid item xs={12} sm={6} md={6} lg={4} key={itemId}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: { xs: 3, md: 4 },
                      overflow: "hidden",
                      border: "1px solid rgba(56, 136, 194, 0.14)",
                      boxShadow: "0 14px 32px rgba(9, 51, 84, 0.09)",
                      background:
                        "linear-gradient(180deg, rgba(247, 251, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)",
                    }}
                  >
                    <Box sx={{ px: 2, pt: 2 }}>
                      <Box
                        sx={{
                          width: "100%",
                          borderRadius: 3,
                          border: `1px solid ${SECONDARY_THEME_COLOR}55`,
                          backgroundColor: "#ffffff",
                          aspectRatio: "16 / 10",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          p: 2,
                        }}
                      >
                        {item.coverImage ? (
                          <Box
                            component="img"
                            src={item.coverImage}
                            alt={item.title}
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "block",
                              objectFit: "contain",
                              objectPosition: "center center",
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Cover coming soon
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
                      <Typography
                        variant="overline"
                        sx={{ color: MAIN_THEME_COLOR, letterSpacing: 1.3, fontWeight: 800 }}
                      >
                        E-Magazine
                      </Typography>
                      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
                        {item.title}
                      </Typography>
                      {item.issue ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                          {item.issue}
                        </Typography>
                      ) : null}
                      {item.releaseDate ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {new Date(item.releaseDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                        {item.description || "Digital Yuvagaaz edition available to view and download."}
                      </Typography>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<VisibilityOutlined />}
                          onClick={async () => {
                            setActiveEMagazineId(itemId);
                            try {
                              await handleOpen(item.pdfUrl);
                            } finally {
                              setActiveEMagazineId(null);
                            }
                          }}
                        >
                          {activeEMagazineId === itemId ? "Opening..." : "View"}
                        </Button>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<DownloadOutlined />}
                          onClick={async () => {
                            setActiveEMagazineId(itemId);
                            try {
                              await handleDownload(item.pdfUrl, item.title);
                            } finally {
                              setActiveEMagazineId(null);
                            }
                          }}
                        >
                          {activeEMagazineId === itemId ? "Preparing..." : "Download"}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <EditorailBoard />
      <EditorsTimeline />
    </MainLayout>
  );
}

export default YuvaGaaz;
